import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import HOSLogo from './HOSLogo';
import { useSiteSettings } from '../context/SiteSettingsContext';
import api from '../utils/api';

// Fallback contact details shown until admin configures them
const FALLBACK_CONTACT = {
  address: '206, Multilevel Shed, Near Lions Club School, GIDC Naroda, Ahmedabad, Gujarat 382330',
  phone:   '+91 85114 28123',
  email:   'sales@houseofsample.in',
  hours:   'Mon–Sat: 9:00 AM – 6:00 PM',
  mapUrl:  '',
};

// Navigation links — matches App.js routes exactly
const NAV_LINKS = [
  { to: '/',          label: 'Home' },
  { to: '/about',     label: 'About Us' },
  { to: '/products',  label: 'Products' },
  { to: '/solutions', label: 'Solutions' },
  { to: '/contact',   label: 'Contact' },
];

// Inline SVG icons for social platforms
const SocialIcon = ({ platform, size = 18 }) => {
  if (!platform) return null;
  const icons = {
    instagram: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
    linkedin:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    facebook:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    youtube:   <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>,
    twitter:   <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    whatsapp:  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
    pinterest: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>,
  };
  return icons[platform] || <span className="text-xs font-bold uppercase">{platform ? platform[0] : '?'}</span>;
};

export default function Footer() {
  const { settings } = useSiteSettings();
  const { socialLinks, siteName, tagline } = settings;
  const [categories, setCategories] = useState([]);

  // Fetch categories from API — same data the admin manages
  useEffect(() => {
    api.get('/categories')
      .then(res => setCategories(res.data.categories || []))
      .catch(() => {});
  }, []);

  // Merge live settings with fallbacks — admin values always win
  const contact = {
    address: settings.contact?.address || FALLBACK_CONTACT.address,
    phone:   settings.contact?.phone   || FALLBACK_CONTACT.phone,
    email:   settings.contact?.email   || FALLBACK_CONTACT.email,
    hours:   settings.contact?.hours   || FALLBACK_CONTACT.hours,
    mapUrl:  settings.contact?.mapUrl  || FALLBACK_CONTACT.mapUrl,
  };

  // Only show social links that are enabled AND have a non-empty URL
  const visibleSocials = Array.isArray(socialLinks)
    ? socialLinks.filter(l => l && l.platform && l.enabled && l.url && l.url.trim().startsWith('http'))
    : [];

  return (
    <footer className="bg-dark-800 border-t border-dark-600 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-10">

        {/* Brand + social */}
        <div>
          <HOSLogo className="mb-4" />
          <p className="text-ink-400 text-sm leading-relaxed mt-4">
            Premium sample solutions for the textile and fashion industry. Quality, precision, and elegance in every sample.
          </p>
          {visibleSocials.length > 0 && (
            <div className="flex gap-3 mt-6 flex-wrap">
              {visibleSocials.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noreferrer"
                  className="text-ink-400 hover:text-gold transition-colors"
                  title={link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}>
                  <SocialIcon platform={link.platform} size={18} />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Navigation — linked to real routes */}
        <div>
          <h4 className="text-xs tracking-widest uppercase text-gold mb-4">Navigation</h4>
          <ul className="space-y-2">
            {NAV_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link to={to} className="text-ink-400 text-sm hover:text-gold transition-colors">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Products — dynamic categories from admin panel */}
        <div>
          <h4 className="text-xs tracking-widest uppercase text-gold mb-4">Products</h4>
          <ul className="space-y-2">
            {categories.length > 0 ? (
              categories.map(cat => (
                <li key={cat.id || cat._id}>
                  <Link
                    to={`/products?category=${cat.id}`}
                    className="text-ink-400 text-sm hover:text-gold transition-colors">
                    {cat.label}
                  </Link>
                </li>
              ))
            ) : (
              // Skeleton while loading
              Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="h-4 w-32 bg-dark-600 animate-pulse rounded" />
              ))
            )}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-xs tracking-widest uppercase text-gold mb-4">Contact</h4>
          <ul className="space-y-3">
            <li className="flex items-start gap-3 text-ink-400 text-sm">
              <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
              <span>{contact.address}</span>
            </li>
            <li className="flex items-center gap-3 text-ink-400 text-sm">
              <Phone size={16} className="text-gold shrink-0" />
              <a href={`tel:${contact.phone}`} className="hover:text-gold transition-colors">{contact.phone}</a>
            </li>
            <li className="flex items-center gap-3 text-ink-400 text-sm">
              <Mail size={16} className="text-gold shrink-0" />
              <a href={`mailto:${contact.email}`} className="hover:text-gold transition-colors">{contact.email}</a>
            </li>
            <li className="flex items-center gap-3 text-ink-400 text-sm">
              <Clock size={16} className="text-gold shrink-0" />
              <span>{contact.hours}</span>
            </li>
          </ul>
          {contact.mapUrl && contact.mapUrl.includes('google.com/maps/embed') && (
            <div className="mt-4 w-full h-36 overflow-hidden rounded-sm border border-dark-600">
              <iframe
                title="Location Map"
                src={contact.mapUrl}
                width="100%" height="100%"
                style={{ border: 0 }}
                allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-dark-600 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-ink-400 text-xs">© {new Date().getFullYear()} {siteName || 'House of Sample'}. All rights reserved.</p>
        <p className="text-ink-400 text-xs italic">{tagline || 'Focus On Quality'}</p>
      </div>
    </footer>
  );
}
